import React, { useEffect } from 'react';
import Score from '../score';
import Banner from '../../Banner';
import { observer } from 'mobx-react';
import { formatDateAndTime } from '../../../utils/helpers';
import pluralize from 'pluralize';
import { useTranslation } from 'react-i18next';
import { Heading1, Heading2, Heading4 } from '@awarego-ui/components';
import { Alert } from '@mui/material';
import ReviewTableSingleThreat from './ReviewTableSingleThreat';
import ReviewTableMultiThreat from './ReviewTableMultiThreat';
import OneMomentSpinner from '../../OneMomentSpinner';

function Review({
    parentUrl,
    assessment,
    companyId,
    assessmentId,
    trainingId,
    assessmentStore,
    preloadedResults,
    shareResults,
    bannerImgSrc,
    bannerLinkURL,
    bannerSnippet,
    language,
    onShowResults,
    isTimedOut,
}) {
    const { loadingAssessmentResults, results, resultsCount } = assessmentStore;
    const scoreReady = !!(preloadedResults || results);
    const { t } = useTranslation();

    useEffect(() => {
        if (!preloadedResults) {
            assessmentStore.loadResults(companyId, assessmentId);
        }
    }, [preloadedResults, companyId, assessmentId]);

    useEffect(() => {
        if (onShowResults) onShowResults();
    }, []);

    // Timed-out assessments were never truly completed — show "time expired on {deadline}"
    // instead of the misleading "completed on {deadline}".
    const completionLabel = isTimedOut && !assessment.completed
        ? t('time expired on')
        : t('completed on');
    const completionDate = isTimedOut && !assessment.completed
        ? assessment.end || assessment.due
        : assessment.completed || assessment.end;

    return (
        <div className="quiz-slide center assessment-review">
            <div className="mobileOnly">
                <Heading2 bold>{assessment.name}</Heading2>
                <p>
                    <i className="date-icon done"></i>
                    {resultsCount} {t(pluralize('question', resultsCount))},{' '}
                    {completionLabel}{' '}
                    {formatDateAndTime(completionDate)}
                </p>
            </div>
            <div className="desktopOnly">
                <Heading1 bold>{assessment.name}</Heading1>
                <br />
                <Heading4 bold>
                    {resultsCount} {t(pluralize('question', resultsCount))},{' '}
                    {completionLabel} {formatDateAndTime(completionDate)}.
                </Heading4>
            </div>
            {isTimedOut && !assessment.completed && (
                <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                    {t('This assessment was not completed — time expired before you could finish. Your score reflects only the questions you answered.')}
                </Alert>
            )}
            {assessment && (
                scoreReady ? (
                    <Score
                        parentUrl={parentUrl}
                        assessment={assessment}
                        horizontal
                        shareResults={shareResults}
                        shareUrl={assessmentStore.shareUrl()}
                    />
                ) : (
                    <OneMomentSpinner />
                )
            )}
            <br className="desktopOnly" />
            {(preloadedResults || results) && (
                <>
                    {Object.keys(preloadedResults || results).length > 1 ? (
                        <ReviewTableMultiThreat
                            loadingAssessmentResults={loadingAssessmentResults}
                            results={
                                preloadedResults ? preloadedResults : results
                            }
                            shareResults={shareResults}
                            parentUrl={parentUrl}
                            trainingId={trainingId}
                            assessmentId={assessment.id}
                        />
                    ) : (
                        <ReviewTableSingleThreat
                            loadingAssessmentResults={loadingAssessmentResults}
                            results={
                                preloadedResults ? preloadedResults : results
                            }
                            shareResults={shareResults}
                            parentUrl={parentUrl}
                            trainingId={trainingId}
                            assessmentId={assessment.id}
                        />
                    )}
                </>
            )}
            {bannerImgSrc &&
                (bannerSnippet && bannerSnippet[language] ? (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: bannerSnippet[language],
                        }}
                    ></div>
                ) : (
                    <Banner
                        language={language}
                        imageSrc={bannerImgSrc}
                        linkUrl={bannerLinkURL}
                    />
                ))}
            <br />
            <br />
        </div>
    );
}

export default observer(Review);
